import type { ButtonProps, ImageProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { LazyComponent } from '@ui/components/LazyComponent';
import { useDefaultAppBar } from '@ui/components/MobileAppBarProvider';
import { SEOTemplate } from '@ui/components/SEO';
import { useWalletModal } from '@ui/components/Wallet';
import { WalletSwitchNetworkDrawer } from '@ui/components/WalletSwitchNetworkDrawer';
import { PATH_RESERVOIR } from '@ui/config/paths';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import { trackEvent } from '@ui/utils/tracking';
import { currentWallet } from '@veno-app/wallet';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { getRenderOptions } from './renderOptions';
import type { ArticleUiType } from './types';
import { isMarketingCampaign } from './types';

export function Article({ article }: { article: ArticleUiType }) {
  useDefaultAppBar();
  const renderOptions = useMemo(() => getRenderOptions(article), [article]);
  // useTrackConnectedCampaignVisitor();

  return (
    <>
      <SEOTemplate title={article.title} description={article.title} />
      <Box
        display={{ base: 'none', desktop: 'initial' }}
        pos="fixed"
        top={0}
        left={0}
        w="100vw"
        h="100vh"
        zIndex={-1}
        bg={{
          desktop:
            'image-set(url(/images/bg/article-bg-desktop.avif) type("image/avif"), url(/images/bg/article-bg-desktop.png) type("image/png")) top right fixed no-repeat',
        }}
      />
      <Box
        bg={{
          base: 'image-set(url(/images/bg/article-bg.avif) type("image/avif"), url(/images/bg/article-bg.png) type("image/png")) top right fixed no-repeat',
          desktop: 'none',
        }}
        px={{ base: 4, desktop: 0 }}
        pb="40px"
      >
        <Flex flexDir="column" gap={4}>
          <Text
            mt={{ base: 2, desktop: 4 }}
            pb={{ desktop: 2 }}
            as="h1"
            textStyle={{
              base: 'h3',
            }}
            textAlign={{ base: 'left', desktop: 'center' }}
          >
            {article.title}
          </Text>
          <CampaignPageImage article={article} />
          <Box
            w={{ base: 'full', desktop: '500px' }}
            mx="auto"
            whiteSpace="pre-wrap"
          >
            <Flex gap={2} flexDir="column" w="full">
              {documentToReactComponents(article.content.json, renderOptions)}
            </Flex>
          </Box>

          <CampaignConnectButton article={article} mt={3} />
        </Flex>
      </Box>
    </>
  );
}

const CampaignPageImage = (props: ImageProps & { article: ArticleUiType }) => {
  const { article, ...imageProps } = props;
  if (!isMarketingCampaign(article)) return null;

  return (
    <Image
      mx="auto"
      w={{ base: 'full', desktop: '600px' }}
      h={{ base: 'auto', desktop: '338px' }}
      alt={imageProps.alt ?? 'Article Banner'}
      src={article.banner.url}
      {...imageProps}
    />
  );
};

const CampaignConnectButton = (
  props: ButtonProps & { article: ArticleUiType },
) => {
  const { article, ...buttonProps } = props;
  const {
    isOpen: isWalletSwitchNetworkDrawerOpen,
    onClose: onWalletSwitchNetworkDrawerClose,
    onOpen: onWalletSwitchNetworkDrawerOpen,
  } = useDisclosure();
  const clickOnCampaignButton = useClickOnCampaignButton(
    onWalletSwitchNetworkDrawerOpen,
  );
  if (!isMarketingCampaign(article)) return null;

  return (
    <>
      <LazyComponent isOpen={isWalletSwitchNetworkDrawerOpen}>
        <WalletSwitchNetworkDrawer
          isOpen={isWalletSwitchNetworkDrawerOpen}
          onClose={onWalletSwitchNetworkDrawerClose}
        />
      </LazyComponent>
      <Button
        w={{ base: 'full', desktop: MAXW_DESKTOP_CTA }}
        mx="auto"
        variant="primary"
        right="0"
        onClick={clickOnCampaignButton}
        aria-label="call to action button"
        {...buttonProps}
      >
        {article.callToAction}
      </Button>
    </>
  );
};

const COOKIE_EXPIRATION_DAYS = 7;
const COOKIE = 'CAMPAIGN_BUTTON_CLICKED';

const useClickOnCampaignButton = (
  onWalletSwitchNetworkDrawerOpen: () => void,
) => {
  const { useIsConnected, useIsConnectedToSupportedChain } = currentWallet;
  const isConnected = useIsConnected();
  const isConnectedToSupportedChain = useIsConnectedToSupportedChain();
  const router = useRouter();
  const { open: openWalletModal } = useWalletModal();

  return useCallback(() => {
    if (isConnected && isConnectedToSupportedChain) {
      router.push(PATH_RESERVOIR);
    } else if (!isConnected) {
      openWalletModal();
    } else {
      onWalletSwitchNetworkDrawerOpen();
    }
    const cookieValue = Cookies.get(COOKIE);
    if (cookieValue) return;
    Cookies.set(COOKIE, 'true', { expires: COOKIE_EXPIRATION_DAYS });
    trackEvent('Clicked Campaign Button');
  }, [
    isConnected,
    isConnectedToSupportedChain,
    onWalletSwitchNetworkDrawerOpen,
    openWalletModal,
    router,
  ]);
};
